import React, { useEffect, useState } from 'react'
import AdminLayout from '../../../layouts/AdminLayout'
import { Accordion, useAccordionButton } from 'react-bootstrap';
import { IconAdjustmentsHorizontal, IconUpload } from '@tabler/icons-react';
import ReservationFilters from '../components/ReservationFilters';
import ReservationTable from '../components/ReservationTable';
import CreateReservation from '../components/CreateReservation';
import { toast } from 'sonner';
// import UpdateReservation from '../components/UpdateReservation';
import UpdateReservation from '../components/update-reservation-v4/UpdateReservationV4';
import useGetAllBookingV4, { DateProp } from '../../../services/create-booking-v4/hooks/useGetAllBookingV4';
import ExportButtonComponent from '../components/ExportButtonComponent';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import { useAuth } from '../../../auth/context/AuthProvider';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import AssignRoomDialog from '../components/assign-room/AssignRoomDialog';
import { BookingV4 } from '../../../services/create-booking-v4/schema/GetAllBookingResponseV4';
import { useDeleteBookingV4 } from '../../../services/create-booking-v4/hooks/useDeleteBookingV4';
import ConfirmationDialog from '../../../shared/components/dialog/ConfirmationDialog';
import ImportBookingDialog from '../components/reservation/ImportBookingDialog';
import { usePropertyReadOnly } from '../../../shared/hooks/usePropertyReadOnly';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

function CustomToggle({ children, eventKey }: { children: React.ReactNode, eventKey: string }) {
    const decoratedOnClick = useAccordionButton(eventKey);

    return (
        <button className="btn btn-icon me-2" onClick={decoratedOnClick}>
            {children}
        </button>
    );
}

const ReservationPages: React.FC = () => {
    const { user } = useAuth();
    const { isReadOnly } = usePropertyReadOnly();

    const [showCreate, setShowCreate] = useState<boolean>(false)
    const [showUpdate, setShowUpdate] = useState<boolean>(false)
    const [idWithUpdate, setIdWithUpdate] = useState<string>('')

    const [search, setSearch] = useState<string>('');
    const [checkinDate, setCheckinDate] = useState<DateProp>();
    const [checkoutDate, setCheckoutDate] = useState<DateProp>();
    const [createdAt, setCreatedAt] = useState<DateProp>();
    const [status, setStatus] = useState<string[]>([
        'confirmed',
        'modified',
    ]);
    const [_properties, setProperties] = useState<string>('');
    const [agents, setAgents] = useState<string>('');
    const [ota_channel, setOtaChannel] = useState<string>('');

    // Sorting
    const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc" | undefined>(undefined);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, _setPerPage] = useState<number>(10);

    // Reset all filters when the active property changes to prevent fetching stale data
    useEffect(() => {
        setSearch('');
        setCheckinDate(undefined);
        setCheckoutDate(undefined);
        setCreatedAt(undefined);
        setStatus(['confirmed', 'modified']);
        setAgents('');
        setOtaChannel('');
        setSortColumn(undefined);
        setSortDirection(undefined);
        setCurrentPage(1);
    }, [user?.active_property_id]);

    if (user?.active_property_id === null) {
        toast.error('Please select a property', { id: 'select-property' });
        return null;
    }

    const { data: bookings, isError, error, isFetching } = useGetAllBookingV4({
        search: search,
        filters: {
            checkin_date: checkinDate,
            checkout_date: checkoutDate,
            created_at: createdAt,
            status: status.join(','),
            property_id: user?.active_property_id,
            agent_id: agents,
            ota_id: ota_channel
        },
        orderBy: sortColumn,
        sort: sortDirection,
        page: currentPage,
        limit: perPage,
    });

    useEffect(() => {
        if (isError && error) {
            toast.error('Failed to fetch bookings', { id: 'fetch-bookings' });
        }
    }, [isError, error]);

    const handleOpenUpdate = (id: string) => {
        setIdWithUpdate(id)
        setShowUpdate(true)
    }

    const handleCloseUpdate = () => {
        setIdWithUpdate('')
        setShowUpdate(false)
    }

    const [showAssignRoom, setShowAssignRoom] = useState<boolean>(false);
    const [reservationDataWillAssign, setReservationDataWillAssign] = useState<BookingV4 | null>(null);

    const handleOpenAssignRoom = (reservation: BookingV4) => {
        if (isReadOnly) return;
        setReservationDataWillAssign(reservation);
        setShowAssignRoom(true);
    }

    const handleCloseAssignRoom = () => {
        setShowAssignRoom(false);
        setReservationDataWillAssign(null);
    }

    const handleFilter = (filter: any) => {
        setSearch(filter.search);
        setCheckinDate(filter.checkin_date);
        setCheckoutDate(filter.checkout_date);
        setCreatedAt(filter.created_at);
        setStatus(filter.status);
        setProperties(filter.property_id);
        setAgents(filter.agent_id);
        setOtaChannel(filter.ota_channel);
        if (filter.show_cancel && !filter.status.includes('cancelled')) {
            setStatus((prev) => [...prev, 'cancelled']);
        }
    }

    const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
    const [reservationIdToDelete, setReservationIdToDelete] = useState<string | null>(null);

    const { mutate: deleteBooking, isPending: isDeleting } = useDeleteBookingV4();

    const handleDeleteBooking = (bookingId: string) => {
        if (isReadOnly) return;
        setReservationIdToDelete(bookingId);
        setShowConfirmDelete(true);
    };

    // BARU: Handler yang akan dipanggil saat pengguna mengkonfirmasi delete dari modal
    const handleConfirmDelete = () => {
        if (isReadOnly) return;
        if (reservationIdToDelete) {
            toast.loading("Deleting reservation...", { id: 'delete-reservation' });
            deleteBooking({
                bookingId: reservationIdToDelete,
                options: {
                    onSettled: () => {
                        setShowConfirmDelete(false);
                        setReservationIdToDelete(null);
                    }
                }
            });
        }
    };

    const handleCloseConfirmDelete = () => {
        setShowConfirmDelete(false);
        setReservationIdToDelete(null);
    };

    const [showImportDialog, setShowImportDialog] = useState<boolean>(false);

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <PropertyInactiveBanner variant="full" />
                <div className="table-container">
                    <Accordion defaultActiveKey="0" flush>
                        <div className="search-container mb-3">
                            <div className="d-flex">
                                <CustomToggle eventKey="0">
                                    <IconAdjustmentsHorizontal className="text-secondary icon" />
                                </CustomToggle>
                                <ExportButtonComponent
                                    search={search}
                                    checkinDate={checkinDate}
                                    checkoutDate={checkoutDate}
                                    createdAt={createdAt}
                                    status={status}
                                    properties={user?.active_property_id ?? ''}
                                    agents={agents}
                                    ota_channel={ota_channel}
                                />
                                {/* <button className="btn bg-cyan text-white">Import</button> */}
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_import']} asComponent>
                                    <ReadOnlyGuard>
                                        <button className="btn btn-outline-secondary ms-2" onClick={() => setShowImportDialog(true)}>
                                            <IconUpload size={16} className="me-1" />
                                            Import
                                        </button>
                                    </ReadOnlyGuard>
                                </RequireAuth>
                            </div>

                            {/* Create button */}
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create</button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>
                        <Accordion.Collapse eventKey="0">
                            <ReservationFilters key={user?.active_property_id} onFilter={handleFilter} />
                        </Accordion.Collapse>
                    </Accordion>
                    <ReservationTable
                        bookings={bookings}
                        onUpdate={handleOpenUpdate}
                        onAssignRoom={handleOpenAssignRoom}
                        setSortDirection={setSortDirection}
                        setSortColumn={setSortColumn}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        isFetching={isFetching}
                        onDelete={handleDeleteBooking}
                        onViewDetail={handleOpenUpdate}
                    />
                    {bookings && bookings.data && bookings.data.length > 0 && bookings.pagination && (
                        <PaginationComponent
                            totalPages={bookings.pagination.last_page}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateReservation show={showCreate} onClose={() => setShowCreate(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            {/* Kept mounted while read-only on purpose: this drawer is also the
                "View Details" surface. Its write controls are gated inside
                CreateBookingComponentV4, so it degrades to view-only. */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                <UpdateReservation show={showUpdate} onClose={handleCloseUpdate} bookingId={idWithUpdate} />
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <AssignRoomDialog show={showAssignRoom} onClose={handleCloseAssignRoom} reservation={reservationDataWillAssign} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <ConfirmationDialog
                        show={showConfirmDelete}
                        onClose={handleCloseConfirmDelete}
                        onConfirm={handleConfirmDelete}
                        title="Confirm Deletion"
                        message="Are you sure you want to permanently delete this reservation? This action cannot be undone."
                        confirmButtonText="Yes, Delete"
                        confirmButtonVariant="danger"
                        isConfirming={isDeleting}
                    />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_import']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <ImportBookingDialog
                        show={showImportDialog}
                        onClose={() => setShowImportDialog(false)}
                        propertyId={user?.active_property_id ?? ''}
                    />
                </ReadOnlyGuard>
            </RequireAuth>
        </AdminLayout>
    )
}

export default ReservationPages